import type {
  DataTableApi,
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from '@components/DataTable';
import {
  ColumnSchema,
  ColumnVisibility,
  nextStoredOrder,
  nextStoredVisibility,
  orderColumnDefs,
  resolveColumnOrder,
  resolveDefaultVisibility,
  resolveVisibilityModel,
} from '@lib/utils/gridColumnPreferences';
import { GridPreferencesTableId, useUIPreferences } from '@stores/UIPreferences';
import { RefObject, useCallback, useMemo, useRef } from 'react';

export interface UseGridColumnPreferencesOptions<R extends GridValidRowModel> extends ColumnSchema {
  /** Which stored slot to use — see `TABLE_PREFERENCE_KEYS` in `UIPreferences`. */
  tableId: GridPreferencesTableId;
  /** The grid's own `useGridApiRef()`, needed to read the post-drag column order. */
  apiRef: RefObject<DataTableApi | null>;
  /** Memoized definitions, in any order. Widths stay local to DataTable. */
  columns: readonly GridColDef<R>[];
  /**
   * Data-driven overrides of `defaultVisibility` — for columns that should default to visible
   * only when the data has something to put in them. Memoise it.
   */
  dynamicVisibility?: Readonly<ColumnVisibility>;
}

export interface GridColumnPreferences<R extends GridValidRowModel> {
  /** Pass straight to `<DataTable columns={…} />` — the defs in the user's order. */
  columns: GridColDef<R>[];
  columnVisibilityModel: GridColumnVisibilityModel;
  onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) => void;
  onColumnOrderChange: () => void;
  /** Restricts the column chooser and hide actions to the persisted schema. */
  getTogglableColumns: (columns: GridColDef[]) => string[];
}

/**
 * Persists a DataGrid's column visibility and order in `UIPreferencesStore`, so the layout
 * survives navigation and reload (and syncs across devices).
 *
 * The rules themselves are pure functions in `@lib/utils/gridColumnPreferences`; this hook is the
 * React/MobX wiring around them. Column *widths* are deliberately not persisted — see the TODO
 * entry for what that would take.
 */
export function useGridColumnPreferences<R extends GridValidRowModel>({
  tableId,
  apiRef,
  columns,
  defaultOrder,
  defaultVisibility,
  dynamicVisibility,
  anchorField,
  leadingFields,
  trailingFields,
}: UseGridColumnPreferencesOptions<R>): GridColumnPreferences<R> {
  const uiPreferences = useUIPreferences();
  // Read during render (and in the dependency arrays below), not only inside a memo factory:
  // React evaluates deps every render, so `observer` tracks these and the backend hydration
  // propagates. A read living only inside a factory is tracked solely in the renders where the
  // factory happens to run.
  const storedVisibility = uiPreferences.tableColumnPrefs[tableId].visibility;
  const storedOrder = uiPreferences.tableColumnPrefs[tableId].order;

  /* ---------------------------------- visibility ---------------------------------- */

  const dynamicDefaults = useMemo(
    () => resolveDefaultVisibility(defaultVisibility, dynamicVisibility),
    [defaultVisibility, dynamicVisibility],
  );
  // Mirrored into a ref so the change handler can diff against current truth without being
  // re-created every time the data flips a dynamic default.
  const dynamicDefaultsRef = useRef(dynamicDefaults);
  dynamicDefaultsRef.current = dynamicDefaults;

  const columnVisibilityModel = useStableVisibilityModel(
    useMemo(
      () => resolveVisibilityModel(storedVisibility, dynamicDefaults),
      [storedVisibility, dynamicDefaults],
    ),
  );

  const onColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      uiPreferences.setTableColumnPreferences(tableId, {
        visibility: nextStoredVisibility(model, dynamicDefaultsRef.current),
      });
    },
    [uiPreferences, tableId],
  );

  /* ------------------------------------ order ------------------------------------- */

  const effectiveOrder = useMemo(
    () =>
      resolveColumnOrder(storedOrder, { defaultOrder, anchorField, leadingFields, trailingFields }),
    [storedOrder, defaultOrder, anchorField, leadingFields, trailingFields],
  );
  const effectiveOrderRef = useRef(effectiveOrder);
  effectiveOrderRef.current = effectiveOrder;

  // There is a sub-second window after mount where the backend preferences haven't arrived, so
  // `effectiveOrder` is still the defaults. A drag inside it persists relative to defaults and is
  // then overwritten when the preferences land. That matches how the visibility preference has
  // behaved since it shipped; blocking interaction during load would be worse.
  const onColumnOrderChange = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const order = nextStoredOrder({
      gridOrder: api.getAllColumns().map((column) => column.field),
      effectiveOrder: effectiveOrderRef.current,
      defaultOrder,
    });
    // `[]` is a legitimate write ("back to defaults"), so this must not be a falsy check.
    if (order === undefined) return;
    uiPreferences.setTableColumnPreferences(tableId, { order });
  }, [apiRef, uiPreferences, tableId, defaultOrder]);

  /* -------------------------------- the columns prop ------------------------------- */

  // Both dependencies are identity-stable by construction: `columns` is the caller's memo, and
  // `effectiveOrder` is the `defaultOrder` reference until something is stored, then changes only
  // when the stored order does. The table treats the definition order as the saved preference.
  const orderedColumns = useMemo(
    () => orderColumnDefs(columns, effectiveOrder),
    [columns, effectiveOrder],
  );

  const getTogglableColumns = useCallback(
    (all: GridColDef[]) =>
      all.filter((column) => column.field in dynamicDefaultsRef.current).map((c) => c.field),
    [],
  );

  return {
    columns: orderedColumns,
    columnVisibilityModel,
    onColumnVisibilityModelChange,
    onColumnOrderChange,
    getTogglableColumns,
  };
}

/**
 * Holds the model's identity steady while its contents are unchanged.
 *
 * `columnVisibilityModel` is a controlled prop, so a
 * fresh-but-equal object each render is a grid state update each render.
 */
function useStableVisibilityModel(model: GridColumnVisibilityModel): GridColumnVisibilityModel {
  const ref = useRef(model);
  if (ref.current !== model && !isSameVisibility(ref.current, model)) ref.current = model;
  return ref.current;
}

function isSameVisibility(a: GridColumnVisibilityModel, b: GridColumnVisibilityModel): boolean {
  const aKeys = Object.keys(a);
  return aKeys.length === Object.keys(b).length && aKeys.every((key) => a[key] === b[key]);
}
